const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'


class ApiService {

    constructor() {
        this.baseURL = API_BASE_URL
        this.token = localStorage.getItem('authToken')
    }

    setToken(token) {
        this.token = token
        localStorage.setItem('authToken', token)
    }

    clearToken() {
        this.token = null
        localStorage.removeItem('authToken')
    }

    async request(endpoind, options = {}) {
        const url = `${this.baseURL}${endpoind}`

        // Always read the latest token from localStorage to ensure persistence across page reloads
        const currentToken = localStorage.getItem('authToken')
        if (currentToken) {
            this.token = currentToken
        }

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options
        }

        if (this.token) {
            config.headers['Authorization'] = `Bearer ${this.token}`
        }

        try {
            const response = await fetch(url, config)

            if (!response.ok) {
                const errorData = await response.json().catch(() => { })

                // Si el token está expirado (401 o 403), cerrar sesión
                if (response.status === 401 || response.status === 403) {
                    console.warn('Token expirado o inválido. Cerrando sesión...')
                    this.clearToken()
                    const path = typeof window !== 'undefined' ? window.location.pathname : ''
                    const redirectTo = path.startsWith('/admin') ? '/admin' : '/login'
                    window.location.href = redirectTo
                    throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.')
                }

                throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
            }

            return await response.json()
        } catch (error) {
            console.error(`Api request failed: ${endpoind}`, error)
            throw error
        }
    }

    async login(email, password) {
        const data = await this.request('/auth/login',
            {
                method: 'POST', body: JSON.stringify({ email, password })
            })

        if (data.session?.access_token) {
            this.setToken(data.session.access_token)
        }

        return data
    }

    async register(userData) {
        return await this.request('/auth/regiser', {
            method: 'POST',
            body: JSON.stringify(userData)
        })
    }

    async logout() {
        this.clearToken()
        try {
            await this.request('/auth/logout', { method: 'POST' })
        } catch (error) {
            console.error('Logout error:', error)
        }
    }

    async getProducts(params = {}) {
        // Limpia los filtros antes de construir la URL
        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(
                ([_, v]) => v !== null && v !== '' && !(typeof v === 'boolean' && v === false)
            )
        );
        const queryParams = new URLSearchParams(cleanParams).toString();
        return await this.request(`/products${queryParams ? `?${queryParams}` : ''}`);
    }

    async getProductImages(productId) {
        return await this.request(`/products/${productId}/images`);
    }

    async getProduct(id) {
        return await this.request(`/products/${id}`)
    }

    async getProductBySlug(slug) {
        return await this.request(`/products/slug/${slug}`);
    }

    async getOrders(params = {}) {
        const queryParams = new URLSearchParams(params).toString();
        return await this.request(`/orders${queryParams ? `?${queryParams}` : ''}`);
    }

    async createOrder(orderData) {
        // Mapear productos para asegurar que los atributos variables se envían como selected_attributes
        let products = orderData.products;
        if (Array.isArray(products)) {
            products = products.map(p => {
                // Si tiene variantesSeleccionadas, renombrar a selected_attributes
                if (p.variantesSeleccionadas) {
                    return {
                        ...p,
                        selected_attributes: p.variantesSeleccionadas,
                        variantesSeleccionadas: undefined
                    };
                }
                return p;
            });
        }
        // Mostrar en consola los productos que se van a enviar
        console.log('Productos enviados a createOrder:', products);
        // Enviar el resto de los datos igual
        return await this.request('/orders', {
            method: 'POST',
            body: JSON.stringify({
                ...orderData,
                products
            })
        });
    }

    async updateOrderStatus(orderId, status) {
        return await this.request('/orders', {
            method: 'PUT',
            body: JSON.stringify({ orderId, status })
        });
    }

    async searchProducts(query, filters = {}) {
        const params = {
            search: query,
            ...filters
        }
        return await this.getProducts(params)
    }

    async createProduct(productData) {
        return await this.request('/products', {
            method: 'POST',
            body: JSON.stringify(productData)
        })
    }

    async updateProduct(id, productData) {
        return await this.request(`/products/${id}`, {
            method: 'PUT',
            body: JSON.stringify(productData)
        })
    }

    async deleteProduct(id) {
        return await this.request(`/products/${id}`, {
            method: 'DELETE'
        })
    }

    async uploadProductImage(productId, imageFile, isPrimary = false) {
        const formData = new FormData()
        formData.append('image', imageFile)
        formData.append('isPrimary', isPrimary.toString())

        // Always read the latest token from localStorage
        const currentToken = localStorage.getItem('authToken')
        if (currentToken) {
            this.token = currentToken
        }

        try {
            const response = await fetch(`${this.baseURL}/products/${productId}/images`, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: this.token ? `Bearer ${this.token}` : undefined
                }
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))

                // Si el token está expirado (401 o 403), cerrar sesión
                if (response.status === 401 || response.status === 403) {
                    console.warn('Token expirado o inválido. Cerrando sesión...')
                    this.clearToken()
                    const path = typeof window !== 'undefined' ? window.location.pathname : ''
                    const redirectTo = path.startsWith('/admin') ? '/admin' : '/login'
                    window.location.href = redirectTo
                    throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.')
                }

                throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
            }

            return await response.json()
        } catch (error) {
            console.error('Error uploading image:', error)
            throw error
        }
    }

    // Categorías
    async getCategories() {
        return await this.request('/categories')
    }

    async createCategory(name, imageFile = null) {
        const slug = name.toLowerCase().replace(/\s+/g, '-');

        // Si hay imagen, usar FormData
        if (imageFile) {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('slug', slug);
            formData.append('image', imageFile);

            // Always read the latest token from localStorage
            const currentToken = localStorage.getItem('authToken');
            if (currentToken) {
                this.token = currentToken;
            }

            try {
                const response = await fetch(`${this.baseURL}/categories`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        Authorization: this.token ? `Bearer ${this.token}` : undefined
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));

                    if (response.status === 401 || response.status === 403) {
                        console.warn('Token expirado o inválido. Cerrando sesión...');
                        this.clearToken();
                        const path = typeof window !== 'undefined' ? window.location.pathname : '';
                        const redirectTo = path.startsWith('/admin') ? '/admin' : '/login';
                        window.location.href = redirectTo;
                        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
                    }

                    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                }

                return await response.json();
            } catch (error) {
                console.error('Error creating category:', error);
                throw error;
            }
        } else {
            // Sin imagen, usar JSON como antes
            return await this.request('/categories', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, slug })
            });
        }
    }

    async deleteCategory(id, imageUrl = null) {
        return await this.request(`/categories?id=${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: imageUrl ? JSON.stringify({ image_url: imageUrl }) : undefined
        });
    }

    // ============= ATRIBUTOS Y OPCIONES =============

    // Atributos de productos
    async getAllProductAttributes() {
        return await this.request('/product-attributes');
    }

    async getProductAttributes(productId) {
        if (!productId) {
            return await this.getAllProductAttributes();
        }
        return await this.request(`/products/${productId}/attributes`);
    }

    async createProductAttribute(productId, attributes) {
        if (productId) {
            return await this.request(`/products/${productId}/attributes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attributes })
            });
        } else {
            // Crear atributo global (sin producto específico)
            const attrData = attributes[0] || {};
            return await this.request('/product-attributes', {
                method: 'POST',
                body: JSON.stringify(attrData)
            });
        }
    }

    async updateProductAttribute(id, updates) {
        return await this.request('/product-attributes', {
            method: 'PUT',
            body: JSON.stringify({ id, ...updates })
        });
    }

    async deleteProductAttribute(id) {
        return await this.request('/product-attributes', {
            method: 'DELETE',
            body: JSON.stringify({ id })
        });
    }
    async getAttributeOptions(attributeId) {
        return await this.request(`/attribute-options${attributeId ? `?attributeId=${attributeId}` : ''}`);
    }

    async createAttributeOption(attributeId, value, displayName, colorHex, imageUrl, displayOrder) {
        return await this.request('/attribute-options', {
            method: 'POST',
            body: JSON.stringify({
                attributeId,
                value,
                displayName,
                colorHex,
                imageUrl,
                displayOrder
            })
        });
    }

    async updateAttributeOption(id, updates) {
        return await this.request('/attribute-options', {
            method: 'PUT',
            body: JSON.stringify({ id, ...updates })
        });
    }

    async deleteAttributeOption(id) {
        return await this.request(`/attribute-options?id=${id}`, {
            method: 'DELETE'
        });
    }

    // ============= VARIANTES DE PRODUCTOS =============

    // Variantes de producto
    async getProductVariants(productId) {
        return await this.request(`/product-variants?productId=${productId}`);
    }

    async createProductVariant(productId, sku, price, stockQuantity, attributes) {
        return await this.request('/product-variants', {
            method: 'POST',
            body: JSON.stringify({
                productId,
                sku,
                price,
                stockQuantity,
                attributes
            })
        });
    }

    async updateProductVariant(id, updates) {
        return await this.request('/product-variants', {
            method: 'PUT',
            body: JSON.stringify({ id, ...updates })
        });
    }

    async deleteProductVariant(id) {
        return await this.request(`/product-variants?id=${id}`, {
            method: 'DELETE'
        });
    }

    // ============= ATRIBUTOS DE VARIANTES =============

    // Atributos específicos de una variante
    async getVariantAttributes(variantId) {
        return await this.request(`/product-variant-attributes?variantId=${variantId}`);
    }

    async createVariantAttribute(variantId, attributeId, optionId) {
        return await this.request('/product-variant-attributes', {
            method: 'POST',
            body: JSON.stringify({
                variantId,
                attributeId,
                optionId
            })
        });
    }

    async updateVariantAttribute(id, optionId) {
        return await this.request('/product-variant-attributes', {
            method: 'PUT',
            body: JSON.stringify({ id, optionId })
        });
    }

    async deleteVariantAttribute(id) {
        return await this.request(`/product-variant-attributes?id=${id}`, {
            method: 'DELETE'
        });
    }

    // ============= OFERTAS =============

    async getActiveOffers() {
        return await this.request('/offers');
    }

    async saveOffer(offerData) {
        return await this.request('/offers', {
            method: 'POST',
            body: JSON.stringify(offerData)
        });
    }

    async deleteOffer(productId) {
        return await this.request(`/offers?product_id=${productId}`, {
            method: 'DELETE'
        });
    }
}

export const apiService = new ApiService()

export async function uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE_URL.replace(/\/api$/, '')}/api/upload-image`, {
        method: 'POST',
        body: formData,
    });
    return await res.json();
}

export async function createProduct(productData) {
    const res = await fetch(`${API_BASE_URL}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData),
    });
    return await res.json();
}

